import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import Lottie, { AnimationConfigWithPath, AnimationEventCallback, AnimationEventName, AnimationItem } from 'lottie-web';

@Component({
  selector: 'shared-lottie',
  template: '',
  styleUrls: ['./lottie.component.scss'],
  standalone: true,
})
export class LottieComponent implements OnInit, OnDestroy, OnChanges {
  private _elementRef = inject(ElementRef);
  private _anim: AnimationItem;

  @Input() jsonPath: string;

  @Input() optionsLottie: { startFrame?: number; endFrame?: number; forceFlag?: boolean } = {};

  private _autoplay = false;
  @Input() set autoplay(val: boolean | string) {
    this._autoplay = val === true || val === 'true';
  }

  private _speed = 1;
  @Input() set speed(val: number | string) {
    if (val) {
      this._speed = typeof val === 'number' ? val : parseInt(val, 10);
      if (this._anim && this._anim.isLoaded) {
        this._anim.setSpeed(this._speed);
      }
    } else {
      this._speed = 1;
    }
  }

  @Input() set frame(frame: number) {
    this._anim.goToAndStop(frame, true);
  }

  @Input() set specificMarkers(markersToCheck: string[]) {
    if (markersToCheck.length && this._anim.isLoaded) {
      // eslint-disable-next-line
      // @ts-ignore
      const { markers } = this._anim;
      if (markers.length) {
        const goodMarkers = [];
        markersToCheck.forEach(mark => {
          const markerToFind = markers.find(elem => elem.payload.name.cm === mark);
          if (markerToFind) {
            goodMarkers.push(markerToFind);
          }
        });
        const timers = goodMarkers.map(elem => elem.time);
        this._anim.playSegments(timers);
      }
    }
  }

  private _loop = false;
  @Input() set loop(val: boolean | string) {
    if (typeof val === 'boolean' || typeof val === 'number') {
      this._loop = val;
    } else {
      if (val === 'true' || val === '') {
        // the empty value means that argument has been added with no value like checked attribute
        this._loop = true;
      } else if (val === 'false') {
        this._loop = false;
      } else {
        console.error(
          'loop parameter is ignored, "',
          val,
          '" is not parsable too boolean, integer or empty string (~true)',
        );
      }
    }
    if (this._anim) {
      this._anim.loop = this._loop;
    }
  }

  private _eventListeners: { name: string; callback: (e: any) => void }[] = [];

  @Output() completed = new EventEmitter<AnimationEventCallback>();
  @Output() loopComplete = new EventEmitter<AnimationEventCallback>();

  ngOnInit(): void {
    this._initLottie();
  }

  ngOnDestroy(): void {
    this._killLottie();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.jsonPath?.previousValue && changes?.jsonPath?.currentValue !== changes?.jsonPath?.previousValue) {
      this._elementRef.nativeElement.getElementsByTagName('svg')[0]?.remove();
      this._initLottie();
    }
  }

  private _initLottie(): void {
    const options: AnimationConfigWithPath = {
      container: this._elementRef.nativeElement,
      renderer: 'svg',
      loop: this._loop,
      autoplay: false,
      // autoloadSegments: true,
      path: this.jsonPath,
    };

    this._anim = Lottie.loadAnimation(options);
    this._anim.addEventListener('DOMLoaded', () => {
      if (this._autoplay) {
        this.play(this.optionsLottie);
      }
      this._anim.setSpeed(this._speed);

      const completeCB = (e): void => this.completed.next(e);
      this._anim.addEventListener('complete', completeCB);
      this._eventListeners.push({ name: 'complete', callback: completeCB });
      const loopCompleteCB = (e): void => this.loopComplete.next(e);
      this._anim.addEventListener('loopComplete', loopCompleteCB);
      this._eventListeners.push({ name: 'looComplete', callback: loopCompleteCB });
    });
  }

  private _killLottie(): void {
    this._eventListeners.forEach(e => this._anim.removeEventListener(<AnimationEventName>e.name, e.callback));
  }

  /**
   * play a subset of frames based on given start and end frame
   */
  play(options: { startFrame?: number; endFrame?: number; forceFlag?: boolean } = {}): void {
    // _play cannot work if markers aren't loaded
    if (this._anim && this._anim.isLoaded) {
      this._play(options);
    } else {
      this._anim.addEventListener('DOMLoaded', () => this._play(options));
    }
  }

  private _play(options: { startFrame?: number; endFrame?: number; forceFlag?: boolean } = {}): void {
    this._anim.playSegments(
      [options.startFrame || 0, options.endFrame || this._anim.totalFrames],
      options.forceFlag || false,
    );
  }
}
